import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFind, useSubscription, useUser } from '/imports/hooks';
import { Dict } from '/imports/types';
import { queryStringToObject } from '/imports/utils';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Button } from '/imports/ui/Button';
import { Checkbox } from '/imports/ui/Checkbox';
import { Select } from '/imports/ui/Select';
import { Modal } from '/imports/ui/Modal';
import { projectManagementLifecycles } from '/imports/api/projectManagementLifecycles';
import { toastOnError } from '/imports/ui/utils';
import { ConversationCandidate, Conversations, listConversationCandidates, startConversation } from '/imports/api/conversations';
import { Publications } from '/imports/publications';
import { ConversationFeed } from '/imports/ui/ConversationFeed';
import { Input } from '/imports/ui/Input';
import { MessageReceipt, MessageReceipts } from '/imports/api/messages';
import { ProjectManagementProjects } from '/imports/api/projectManagement';

const MessagesPageStyled = styled(Page)`
  /* One row across the body: the search takes what the filters and the button leave, so the button
     ends at the right edge like the action in every other list page's filter row. */
  .toolbar {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    .search { flex: 1 1 16rem; }
    .search > div, .search input { width: 100%; box-sizing: border-box; }
    .vertical { min-width: 14rem; }
    .btn { margin-left: auto; white-space: nowrap; }
    /* The Checkbox brings its own label and a slight upward nudge meant for inline text; here the box and
       the words sit on one flex line, centred, so the nudge comes off. */
    .unread-only {
      font-size: 0.9rem; white-space: nowrap;
      label { display: inline-flex; align-items: center; cursor: pointer; }
      input { top: 0; margin-right: 0.4rem; }
    }
  }
  .empty {
    color: ${css.grey4};
    padding: 2rem 0;
  }
  .conversation {
    position: relative;
    border-bottom: 1px solid ${css.grey4};
    padding: 1rem 1rem 1rem 2rem;
    cursor: pointer;
    &:first-child {
      border-top: 1px solid ${css.grey4};
    }
    .name {
      font-weight: bold;
    }
    .last-message {
      color: ${css.grey4};
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
    .badge {
      position: absolute;
      background: ${css.primaryColor};
      color: ${css.white};
      top: 0.1rem;
      right: 0;
      border-radius: 0.75rem;
      min-width: 1.5rem;
      height: 1.5rem;
      text-align: center;
    }
  }
`;

/** Page listing conversations with unread badges and an open conversation feed. */
export const MessagesPage = () => {
  const user = useUser();
  const query = queryStringToObject();
  const [conversationId, setConversationId] = useState<string | undefined>(query.c || undefined);
  const loadingConversations = useSubscription(Publications.conversations);
  const conversations = useFind(() => Conversations.find({}, { sort: { lastMessageAt: -1 } }), [loadingConversations]);
  // Project chats are titled by the project itself — Luminia id and current name — not the name snapshot
  // the conversation was created with.
  const projects = useFind(() => ProjectManagementProjects.find({}, { fields: { name: 1, luminiaId: 1, lifecycleKey: 1 } }), [loadingConversations]);
  const titleOf = (c: { name?: string, projectManagementProjectId?: string }) => {
    const p = c.projectManagementProjectId ? projects.find((x) => x._id === c.projectManagementProjectId) : undefined;
    if (!p) return c.name || '';
    return `${p.luminiaId ? `[${p.luminiaId}] ` : ''}${p.name || ''}`;
  };
  const loadingMessageReceipts = useSubscription(Publications.unreadMessageReceipts);
  const unreadReceipts = useFind(() => MessageReceipts.find(), [loadingMessageReceipts]);
  const loading = loadingConversations || loadingMessageReceipts;
  const unreadByConversation = unreadReceipts.reduce((map: Dict<MessageReceipt[]>, r) => {
    const conversationId = r.conversationId;
    if (!map[conversationId]) map[conversationId] = [];
    map[conversationId].push(r);
    return map;
  }, {});
  const [search, setSearch] = useState('');
  const [starting, setStarting] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [vertical, setVertical] = useState('');
  const q = search.trim().toLowerCase();
  // Only the verticals actually present among the listed chats are offered.
  const verticals = [...new Set(projects.map((p) => p.lifecycleKey).filter(Boolean))].map((key) => ({ value: key, label: projectManagementLifecycles[key]?.label || key }));
  const projectOf = (c: { projectManagementProjectId?: string }) => (c.projectManagementProjectId ? projects.find((p) => p._id === c.projectManagementProjectId) : undefined);
  const visibleConversations = conversations
    .filter((c) => !q || titleOf(c).toLowerCase().includes(q) || (c.lastMessage?.content || '').toLowerCase().includes(q))
    .filter((c) => !unreadOnly || unreadByConversation[c._id]?.length)
    .filter((c) => !vertical || projectOf(c)?.lifecycleKey === vertical);
  return (
    <MessagesPageStyled>
      <PageHeading
        left={
          <h1>{strings.Messages()}</h1>
        }
      />
      <PageBody>
        {
          loading ?
            <LoadingFlex />
            :
            <div>
              <div className="toolbar">
                <div className="search"><Input value={search} placeholder={strings.Search()} onChange={(v) => setSearch(v || '')} /></div>
                {
                  verticals.length > 1 &&
                  <div className="vertical"><Select value={vertical} options={[{ value: '', label: strings.AllVerticals() }, ...verticals]} onChange={(v) => setVertical(v || '')} /></div>
                }
                <div className="unread-only"><Checkbox checked={unreadOnly} onChange={(v) => setUnreadOnly(v)}>{strings.UnreadOnly()}</Checkbox></div>
                <Button className="primary" onClick={() => setStarting(true)}>{strings.NewConversation()}</Button>
              </div>
              {!conversations.length && <div className="empty">{strings.NoConversationsYet()}</div>}
              {
                visibleConversations.map((c) => {
                  return (
                    <div key={c._id} className="conversation" onClick={() => setConversationId(c._id)}>
                      <div className="name">{titleOf(c)}</div>
                      {
                        c.lastMessage ?
                          <div className="last-message">
                            {c.lastMessage?.senderUserId === user?._id ? strings.You() : c.lastMessage?.senderName}: {c.lastMessage?.content}
                          </div>
                          :
                          <div className="last-message">&nbsp;</div>
                      }
                      {
                        unreadByConversation[c._id]?.length ?
                          <div className="badge">{unreadByConversation[c._id].length}</div>
                          :
                          null
                      }
                    </div>
                  );
                })
              }
            </div>
        }
      </PageBody>
      {
        conversationId ?
          <ConversationFeed
            key={conversationId}
            conversationId={conversationId}
            showProjectLink
            onClose={() => setConversationId(undefined)}
          />
          :
          null
      }
      {
        starting &&
        <NewConversationModal
          onClose={() => setStarting(false)}
          onStarted={(id) => { setStarting(false); setConversationId(id); }}
        />
      }
    </MessagesPageStyled>
  );
};

const NewConversationStyled = styled.div`
  .picker { margin-bottom: 1rem; }
  /* In flow, not floating: a floating menu covered the name field and the buttons, so a click meant for
     them landed on an option. */
  .picker-menu {
    margin-top: 0.2rem;
    background: ${css.white}; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius};
    max-height: 14rem; overflow: auto;
    .opt { padding: 0.4rem 0.75rem; cursor: pointer; }
    .opt:hover { background: ${css.grey8}; }
    .none { padding: 0.4rem 0.75rem; color: ${css.grey4}; }
  }
  .chips { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1rem; min-height: 1.5rem; }
  .chip {
    display: inline-flex; align-items: center; gap: 0.25rem;
    background: ${css.grey8}; border: 1px solid ${css.grey6}; border-radius: 1rem;
    padding: 0.05rem 0.3rem 0.05rem 0.55rem;
  }
  .chip-remove { background: none; border: none; cursor: pointer; color: ${css.grey4}; font-size: 0.7rem; padding: 0 0.15rem; }
  .chip-remove:hover { color: ${css.red2}; }
  .name { margin-bottom: 1rem; }
  .actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
`;

/**
 * Starts an ad-hoc conversation: pick people (the org's accounts; super users see everyone), name it or let
 * the names name it. The same set of people reopens their existing conversation instead of making another.
 */
const NewConversationModal = (props: { onClose: () => void, onStarted: (conversationId: string) => void }) => {
  const [candidates, setCandidates] = useState<ConversationCandidate[]>([]);
  const [chosen, setChosen] = useState<ConversationCandidate[]>([]);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { toastOnError(async () => setCandidates(await listConversationCandidates.call(undefined)))(); }, []);
  const q = search.trim().toLowerCase();
  const matches = q
    ? candidates.filter((c) => !chosen.some((p) => p.kind === c.kind && p._id === c._id)).filter((c) => `${c.name} ${c.email || ''} ${c.org || ''}`.toLowerCase().includes(q)).slice(0, 8)
    : [];
  const describe = (c: ConversationCandidate) => `${c.name}${c.email ? ` · ${c.email}` : ''}${c.org ? ` · ${c.org}` : ''}${c.kind === 'superUser' ? ` · ${strings.SuperUser()}` : ''}`;
  const start = toastOnError(async () => {
    setBusy(true);
    try {
      const id = await startConversation.call({
        accountIds: chosen.filter((c) => c.kind === 'account').map((c) => c._id),
        userIds: chosen.filter((c) => c.kind === 'superUser').map((c) => c._id),
        name: name.trim() || undefined,
      });
      props.onStarted(id);
    }
    finally {
      setBusy(false);
    }
  });
  return (
    <Modal title={strings.NewConversation()} onClose={props.onClose}>
      <NewConversationStyled>
        <div className="chips">
          {chosen.map((c) => (
            <span key={`${c.kind}_${c._id}`} className="chip">
              {c.name}{c.kind === 'superUser' ? ` · ${strings.SuperUser()}` : ''}
              <button className="chip-remove" onClick={() => setChosen(chosen.filter((p) => !(p.kind === c.kind && p._id === c._id)))}>✕</button>
            </span>
          ))}
        </div>
        <div className="picker">
          {/* The menu closes when the field loses focus — a beat late, so a click on an option still lands. */}
          <Input value={search} placeholder={strings.AddPeople()} onChange={(v) => { setSearch(v || ''); setMenuOpen(true); }}
            onFocus={() => setMenuOpen(true)} onBlur={() => setTimeout(() => setMenuOpen(false), 150)} />
          {
            q && menuOpen &&
            <div className="picker-menu">
              {
                matches.length
                  ? matches.map((c) => (
                    <div key={`${c.kind}_${c._id}`} className="opt" onMouseDown={(e) => e.preventDefault()} onClick={() => { setChosen([...chosen, c]); setSearch(''); }}>
                      {describe(c)}
                    </div>
                  ))
                  : <div className="none">{strings.NoResults()}</div>
              }
            </div>
          }
        </div>
        <div className="name"><Input value={name} placeholder={strings.ConversationNameOptional()} onChange={(v) => setName(v || '')} /></div>
        <div className="actions">
          <Button onClick={props.onClose}>{strings.Cancel()}</Button>
          <Button className="primary" disabled={!chosen.length || busy} onClick={() => start()}>{strings.StartConversation()}</Button>
        </div>
      </NewConversationStyled>
    </Modal>
  );
};
